import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ApiError, AuthUser, api } from './lib/api'
import { clearAllCaches } from './lib/localCache'

type AuthStatus = 'checking' | 'signed-in' | 'signed-out' | 'unreachable'

interface AuthContextValue {
  status: AuthStatus
  user: AuthUser | null
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  /**
   * Deletes the account on the server, then forgets it here. Rejects — leaving
   * everything as it was — when the password is wrong or the server can't be reached.
   */
  deleteAccount: (password: string) => Promise<void>
  /** Re-reads the session — used after confirming an email address. */
  refresh: () => Promise<void>
  /** Something to tell the person on the signed-out screen, such as that their account is gone. */
  notice: string | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

/** The account and view App remembers per user (see `remember` there). */
function forgetViewFor(userId: string) {
  try {
    localStorage.removeItem(`snapshot-money:scope:${userId}`)
    localStorage.removeItem(`snapshot-money:view:${userId}`)
  } catch {
    // Nothing kept, nothing to forget.
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking')
  const [user, setUser] = useState<AuthUser | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const { user } = await api.me()
      setUser(user)
      setStatus(user ? 'signed-in' : 'signed-out')
    } catch (e) {
      // A server that can't be reached is not the same as being signed out,
      // and showing a login form here would be a lie: signing in wouldn't
      // work either. The app says so instead and offers to retry.
      setUser(null)
      setStatus(e instanceof ApiError && e.isNetworkFailure ? 'unreachable' : 'signed-out')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      notice,
      refresh: load,
      signIn: async (email, password) => {
        const { user } = await api.login(email, password)
        setNotice(null)
        setUser(user)
        setStatus('signed-in')
      },
      signUp: async (email, password) => {
        await api.register(email, password)
        // Registering doesn't start a session, so sign in straight after —
        // being bounced to a login form after filling in the same details is
        // an annoyance with nothing to recommend it.
        const { user } = await api.login(email, password)
        setUser(user)
        setStatus('signed-in')
      },
      signOut: async () => {
        try {
          await api.logout()
        } finally {
          setUser(null)
          setStatus('signed-out')
        }
      },
      deleteAccount: async (password) => {
        await api.deleteAccount(password)
        // The offline copy is the last trace of the plan on this device.
        clearAllCaches()
        if (user) forgetViewFor(user.id)
        setNotice('Your account has been deleted, along with your plan and sandbox.')
        setUser(null)
        setStatus('signed-out')
      },
    }),
    [status, user, notice, load],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
